import React from "react";
import { motion } from "motion/react";
import {
    FiMail,
    FiClock,
    FiGlobe,
    FiAlertCircle,
    FiArrowRight,
    FiCopy,
    FiCheckCircle,
    FiHelpCircle,
    FiHeart,
    FiZap,
} from "react-icons/fi";
import { HiSparkles } from "react-icons/hi2";
import ClientNavbar from "../components/Navbar/ClinetNavbar";
import FixedButton from "../components/Client/FixedButton";
import ClientFooter from "../components/Client/ClientFooter";
import ClientAdSense from "../components/Client/ClientAdSense";
import insta from "../Image/Client/insta.svg";
import fb from "../Image/Client/fb.svg";
import thr from "../Image/Client/thr.svg";

const EMAIL = "picpromptify@gmail.com";

const HIGHLIGHTS = [
    {
        icon: FiZap,
        title: "Fast Replies",
        desc: "We respond within 1–2 business days.",
        gradient: "linear-gradient(135deg, #7F00FF, #FF8AFF)",
    },
    {
        icon: FiHeart,
        title: "Always Free",
        desc: "Support, suggestions, ideas — all welcome.",
        gradient: "linear-gradient(135deg, #FCFA02, #FF8AFF)",
    },
    {
        icon: FiHelpCircle,
        title: "Anything Goes",
        desc: "Bugs, feedback, feature ideas, or just hi.",
        gradient: "linear-gradient(135deg, #FF8AFF, #7F00FF)",
    },
];

const SOCIALS = [
    {
        icon: insta,
        alt: "Instagram",
        url: "https://www.instagram.com/picpromptify/",
        brand: "linear-gradient(135deg, #F58529, #DD2A7B, #8134AF)",
    },
    {
        icon: fb,
        alt: "Facebook",
        url: "https://www.facebook.com/profile.php?id=61579052481615",
        brand: "linear-gradient(135deg, #1877F2, #4267B2)",
    },
    {
        icon: thr,
        alt: "Threads",
        url: "https://www.threads.com/@picpromptify",
        brand: "linear-gradient(135deg, #000000, #555555)",
    },
];

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.2 },
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
};

const ContactUs = () => {
    const [copied, setCopied] = React.useState(false);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(EMAIL);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {}
    };

    return (
        <div className='min-h-screen dm-sans client-home-bg'>
            <ClientNavbar />
            <FixedButton />

            {/* HERO */}
            <section className='relative w-full overflow-hidden'>
                <motion.div
                    aria-hidden
                    animate={{ x: [0, 40, -20, 0], y: [0, -30, 30, 0] }}
                    transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
                    className='absolute -top-10 -left-20 w-80 h-80 rounded-full opacity-35 blur-3xl pointer-events-none'
                    style={{
                        background:
                            "radial-gradient(circle, #7F00FF 0%, transparent 70%)",
                    }}
                />
                <motion.div
                    aria-hidden
                    animate={{ x: [0, -40, 30, 0], y: [0, 30, -40, 0] }}
                    transition={{ duration: 24, repeat: Infinity, ease: "easeInOut" }}
                    className='absolute top-10 -right-20 w-96 h-96 rounded-full opacity-25 blur-3xl pointer-events-none'
                    style={{
                        background:
                            "radial-gradient(circle, #FCFA02 0%, transparent 70%)",
                    }}
                />

                <div className='relative mx-auto max-w-5xl px-4 sm:px-6 md:px-8 pt-8 md:pt-14 pb-6 md:pb-10 text-center'>
                    {/* DSPY1 — top-of-page display ad */}
                    <ClientAdSense
                        slot='9729451986'
                        containerClassName='w-full mb-6 md:mb-10 text-left'
                    />

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className='inline-flex items-center gap-2 ios-glass px-3 py-1.5 rounded-full text-[10px] md:text-xs text-client-font/85 mb-4'>
                            <HiSparkles style={{ color: "#FCFA02" }} />
                            <span className='tracking-wider uppercase'>Let's Talk</span>
                        </div>
                        <h1 className='text-4xl md:text-6xl font-bold text-client-font tracking-tight leading-tight'>
                            We'd love to{" "}
                            <span
                                style={{
                                    background:
                                        "linear-gradient(90deg, #FCFA02, #FF8AFF, #7F00FF)",
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    backgroundClip: "text",
                                }}
                            >
                                hear from you
                            </span>
                        </h1>
                        <p className='text-client-font/60 text-sm md:text-lg mt-4 max-w-2xl mx-auto leading-relaxed'>
                            Have a question, suggestion, or just want to say hi? Reach out
                            and we'll get back to you within a couple of business days.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* MAIN CONTACT CARD */}
            <section className='relative w-full px-4 sm:px-6 md:px-8 pb-8 md:pb-12'>
                <div className='mx-auto max-w-4xl'>
                    <motion.div
                        {...fadeUp}
                        className='relative rounded-3xl p-6 md:p-10 overflow-hidden'
                        style={{
                            background:
                                "linear-gradient(135deg, rgba(127,0,255,0.22), rgba(252,250,2,0.06))",
                            border: "1px solid rgba(127,0,255,0.35)",
                            backdropFilter: "blur(20px) saturate(180%)",
                            WebkitBackdropFilter: "blur(20px) saturate(180%)",
                            boxShadow:
                                "0 24px 60px -16px rgba(127,0,255,0.5), 0 12px 28px -10px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.16)",
                        }}
                    >
                        <motion.div
                            aria-hidden
                            animate={{ rotate: 360 }}
                            transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
                            className='absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full opacity-30 blur-3xl pointer-events-none'
                            style={{
                                background:
                                    "conic-gradient(from 0deg, #7F00FF, #FF8AFF, #FCFA02, #7F00FF)",
                            }}
                        />

                        <div className='relative flex flex-col md:flex-row items-start md:items-center gap-5 md:gap-7'>
                            <motion.div
                                animate={{ rotate: [0, 8, -8, 0] }}
                                transition={{
                                    duration: 3.5,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                }}
                                className='w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center flex-shrink-0'
                                style={{
                                    background:
                                        "linear-gradient(135deg, #FCFA02, #FF8AFF, #7F00FF)",
                                    boxShadow:
                                        "0 14px 32px -8px rgba(127,0,255,0.65), inset 0 1px 0 rgba(255,255,255,0.3)",
                                }}
                            >
                                <FiMail className='text-2xl md:text-3xl text-white drop-shadow-lg' />
                            </motion.div>
                            <div className='flex-1 min-w-0 w-full'>
                                <p className='text-client-title text-[10px] md:text-xs uppercase tracking-[0.2em] font-semibold mb-1'>
                                    Email us
                                </p>
                                <h2 className='text-client-font font-bold text-2xl md:text-3xl tracking-tight leading-tight mb-3'>
                                    Drop us a line
                                </h2>

                                {/* Email row */}
                                <div
                                    className='flex flex-col sm:flex-row items-stretch sm:items-center gap-2 rounded-2xl p-2 md:p-2.5'
                                    style={{
                                        background: "rgba(0,0,0,0.25)",
                                        border: "1px solid rgba(255,255,255,0.1)",
                                        backdropFilter: "blur(12px)",
                                    }}
                                >
                                    <a
                                        href={`mailto:${EMAIL}`}
                                        className='flex-1 min-w-0 px-3 py-2 text-client-font font-semibold text-sm md:text-base break-all hover:text-client-title transition-colors'
                                    >
                                        {EMAIL}
                                    </a>
                                    <div className='flex items-center gap-2'>
                                        <motion.button
                                            onClick={copy}
                                            whileHover={{ y: -2, scale: 1.03 }}
                                            whileTap={{ scale: 0.95 }}
                                            className='ios-glass-btn px-3 py-2 rounded-xl text-client-font text-xs md:text-sm font-medium inline-flex items-center gap-1.5'
                                        >
                                            {copied ? (
                                                <>
                                                    <FiCheckCircle
                                                        style={{ color: "#FCFA02" }}
                                                    />
                                                    Copied
                                                </>
                                            ) : (
                                                <>
                                                    <FiCopy />
                                                    Copy
                                                </>
                                            )}
                                        </motion.button>
                                        <motion.a
                                            href={`mailto:${EMAIL}`}
                                            whileHover={{ y: -2, scale: 1.03 }}
                                            whileTap={{ scale: 0.95 }}
                                            className='inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-white text-xs md:text-sm font-semibold'
                                            style={{
                                                background:
                                                    "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                                                boxShadow:
                                                    "0 10px 24px -6px rgba(127,0,255,0.55), inset 0 1px 0 rgba(255,255,255,0.25)",
                                            }}
                                        >
                                            Send <FiArrowRight />
                                        </motion.a>
                                    </div>
                                </div>

                                <div
                                    className='inline-flex items-center gap-2 mt-4 px-3 py-1.5 rounded-full text-[10px] md:text-xs text-client-font/75'
                                    style={{
                                        background: "rgba(255,255,255,0.05)",
                                        border: "1px solid rgba(255,255,255,0.1)",
                                    }}
                                >
                                    <FiClock className='text-[11px]' />
                                    Replies within 1–2 business days
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* HIGHLIGHTS GRID */}
            <section className='relative w-full px-4 sm:px-6 md:px-8 pb-10 md:pb-16'>
                <div className='mx-auto max-w-5xl'>
                    <div className='grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4'>
                        {HIGHLIGHTS.map((h, i) => {
                            const Icon = h.icon;
                            return (
                                <motion.div
                                    key={h.title}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.2 }}
                                    transition={{ duration: 0.5, delay: i * 0.08 }}
                                    whileHover={{ y: -5 }}
                                    className='ios-glass-dark relative rounded-2xl p-4 md:p-5 overflow-hidden group'
                                    style={{
                                        boxShadow:
                                            "0 10px 26px -10px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)",
                                    }}
                                >
                                    <div
                                        aria-hidden
                                        className='absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-0 group-hover:opacity-30 transition-opacity duration-500 blur-3xl pointer-events-none'
                                        style={{ background: h.gradient }}
                                    />
                                    <motion.div
                                        whileHover={{ rotate: 8, scale: 1.05 }}
                                        transition={{
                                            type: "spring",
                                            stiffness: 300,
                                            damping: 14,
                                        }}
                                        className='w-11 h-11 rounded-2xl flex items-center justify-center mb-3'
                                        style={{
                                            background: h.gradient,
                                            boxShadow:
                                                "0 8px 22px -6px rgba(127,0,255,0.45)",
                                        }}
                                    >
                                        <Icon className='text-lg text-white' />
                                    </motion.div>
                                    <h3 className='text-client-font font-bold text-base md:text-lg mb-1'>
                                        {h.title}
                                    </h3>
                                    <p className='text-client-font/60 text-sm leading-relaxed'>
                                        {h.desc}
                                    </p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* TWO-COLUMN: ABOUT + SOCIALS */}
            <section className='relative w-full px-4 sm:px-6 md:px-8 pb-12 md:pb-20'>
                <div className='mx-auto max-w-5xl grid md:grid-cols-2 gap-4 md:gap-5'>
                    {/* About this site */}
                    <motion.div
                        {...fadeUp}
                        className='ios-glass-dark rounded-3xl p-5 md:p-7 relative overflow-hidden'
                    >
                        <div
                            aria-hidden
                            className='absolute -top-16 -right-12 w-44 h-44 rounded-full opacity-25 blur-3xl pointer-events-none'
                            style={{
                                background:
                                    "radial-gradient(circle, #7F00FF 0%, transparent 70%)",
                            }}
                        />
                        <div className='relative flex items-center gap-2.5 mb-4'>
                            <div
                                className='w-9 h-9 rounded-xl flex items-center justify-center'
                                style={{
                                    background:
                                        "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                                    boxShadow:
                                        "0 6px 16px -4px rgba(127,0,255,0.55)",
                                }}
                            >
                                <FiGlobe className='text-base text-white' />
                            </div>
                            <h3 className='text-client-font font-bold text-base md:text-lg tracking-tight'>
                                About this site
                            </h3>
                        </div>

                        <ul className='relative space-y-2.5 text-sm md:text-[15px]'>
                            <li className='flex items-start gap-3'>
                                <span className='text-client-font/55 text-xs uppercase tracking-wider min-w-[68px] mt-0.5'>
                                    Website
                                </span>
                                <a
                                    href='https://picpromptify.com/'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='text-client-font font-semibold hover:text-client-title transition-colors break-all'
                                >
                                    picpromptify.com
                                </a>
                            </li>
                            <li className='flex items-start gap-3'>
                                <span className='text-client-font/55 text-xs uppercase tracking-wider min-w-[68px] mt-0.5'>
                                    Service
                                </span>
                                <span className='text-client-font/85'>
                                    Free AI Image Prompt Generator (text-only)
                                </span>
                            </li>
                        </ul>

                        <div
                            className='relative mt-5 pt-4 text-xs md:text-sm text-client-font/65'
                            style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}
                        >
                            We don't collect personal data beyond standard analytics. For
                            full details, see our{" "}
                            <a
                                href='/privacy-policy'
                                className='font-semibold underline decoration-2 underline-offset-2'
                                style={{ color: "#FCFA02" }}
                            >
                                Privacy Policy
                            </a>{" "}
                            and{" "}
                            <a
                                href='/terms'
                                className='font-semibold underline decoration-2 underline-offset-2'
                                style={{ color: "#FCFA02" }}
                            >
                                Terms
                            </a>
                            .
                        </div>
                    </motion.div>

                    {/* Socials + note */}
                    <motion.div
                        {...fadeUp}
                        className='ios-glass-dark rounded-3xl p-5 md:p-7 relative overflow-hidden'
                    >
                        <div
                            aria-hidden
                            className='absolute -bottom-16 -left-12 w-44 h-44 rounded-full opacity-20 blur-3xl pointer-events-none'
                            style={{
                                background:
                                    "radial-gradient(circle, #FCFA02 0%, transparent 70%)",
                            }}
                        />
                        <div className='relative flex items-center gap-2.5 mb-4'>
                            <div
                                className='w-9 h-9 rounded-xl flex items-center justify-center'
                                style={{
                                    background:
                                        "linear-gradient(135deg, #FCFA02, #FF8AFF)",
                                    boxShadow:
                                        "0 6px 16px -4px rgba(252,250,2,0.5)",
                                }}
                            >
                                <FiHeart className='text-base text-white' />
                            </div>
                            <h3 className='text-client-font font-bold text-base md:text-lg tracking-tight'>
                                Follow us
                            </h3>
                        </div>
                        <p className='relative text-client-font/65 text-sm leading-relaxed mb-4'>
                            Stay updated with the latest prompts, drops, and creative
                            inspiration.
                        </p>
                        <div className='relative flex flex-wrap gap-2.5'>
                            {SOCIALS.map((s) => (
                                <motion.button
                                    key={s.alt}
                                    onClick={() => window.open(s.url, "_blank")}
                                    whileHover={{ y: -4, scale: 1.08 }}
                                    whileTap={{ scale: 0.92 }}
                                    aria-label={s.alt}
                                    className='group relative w-11 h-11 rounded-2xl flex items-center justify-center overflow-hidden'
                                    style={{
                                        background: "rgba(255,255,255,0.06)",
                                        border: "1px solid rgba(255,255,255,0.14)",
                                        boxShadow:
                                            "0 8px 20px -8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.18)",
                                    }}
                                >
                                    <div
                                        aria-hidden
                                        className='absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300'
                                        style={{ background: s.brand }}
                                    />
                                    <img
                                        src={s.icon}
                                        alt={s.alt}
                                        className='relative w-4 transition-transform duration-300 group-hover:scale-110'
                                    />
                                </motion.button>
                            ))}
                        </div>

                        <div
                            className='relative mt-5 pt-4 flex items-start gap-2.5 text-xs md:text-sm text-client-font/65'
                            style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}
                        >
                            <FiAlertCircle
                                className='text-base flex-shrink-0 mt-0.5'
                                style={{ color: "#FCFA02" }}
                            />
                            <p>
                                <span className='text-client-title font-semibold'>
                                    Heads up:
                                </span>{" "}
                                PicPromptify doesn't generate or store images. We only
                                provide text prompts for use with external AI tools.
                            </p>
                        </div>
                    </motion.div>
                </div>
            </section>

            <ClientFooter />
        </div>
    );
};

export default ContactUs;
